import {FC, RefObject, useState} from 'react';
import {Box, Button, Center, Flex, Grid, Stack} from '@chakra-ui/react';
import {api, useT} from '@kcast-fe/core';
import {Loader, SearchInputBig, Svg, useScreenSize} from 'ui-kit';

import {SummaryListItem, SUMMARY_GRID_TEMPLATE} from './components';

interface IProps {
  onSelect: (id: string) => void;
  onNew: () => void;
  containerRef: RefObject<HTMLDivElement | null>;
}

export const SummaryList: FC<IProps> = ({onSelect, onNew, containerRef}) => {
  const {data: summaries = [], isLoading} = api.summary.useSummaryListQuery();
  const {data: settings} = api.settings.useSettingsQuery();

  const [query, setQuery] = useState('');

  const {isLessThan768} = useScreenSize();
  const {t} = useT();

  const sortedItems = [...summaries].sort((a, b) => a.title.localeCompare(b.title));
  const filteredItems = query
    ? sortedItems.filter((d) => d.title.toLowerCase().includes(query.toLowerCase()))
    : sortedItems;

  return (
    <Stack h={'100%'} gap={'0'}>
      {summaries.length > 0 && (
        <Box p={isLessThan768 ? '12px 8px 0 8px' : '16px 20px 6px 20px'}>
          <SearchInputBig value={query} onChange={setQuery} />
        </Box>
      )}

      {filteredItems.length > 0 && (
        <Box p={isLessThan768 ? '6px 12px 0 12px' : '12px 24px 0 24px'}>
          <Grid
            h={'32px'}
            gap={'8px'}
            bg={'{colors.bgMain}'}
            alignItems={'center'}
            borderRadius={'4px'}
            borderTop={'thin solid'}
            borderBottom={'thin solid'}
            borderColor={'{colors.borderDefault}'}
            gridTemplateColumns={SUMMARY_GRID_TEMPLATE}
            color={'{colors.textSecondary}'}
          >
            <Box pl={'12px'}>{t('summary.list.titleCol')}</Box>
            {!isLessThan768 && <Box>{t('summary.list.statusCol')}</Box>}
            {!isLessThan768 && <Box>{t('summary.list.updatedCol')}</Box>}
            <Box>{t('summary.list.enabledCol')}</Box>
            <Box />
          </Grid>
        </Box>
      )}

      <Box flex={'1 1 auto'} overflowY={'auto'} p={isLessThan768 ? '0 12px' : '0 24px'}>
        {isLoading && (
          <Center h={'100%'}>
            <Loader size={'lg'} />
          </Center>
        )}

        {!isLoading && summaries.length === 0 && (
          <Center flexDirection={'column'} gap={'24px'} h={'100%'} textAlign={'center'}>
            <Box fontWeight={'500'} color={'{colors.textPrimary}'}>
              {t('summary.list.empty')}
            </Box>
            <Box color={'{colors.textSecondary}'} maxW={'400px'}>
              {t('summary.list.emptyDescription', {name: settings?.assistant?.name})}
            </Box>
            <Button size={'lg'} variant={'solid'} onClick={onNew}>
              <Svg name={'Plus'} size={'s20'} />
              {t('summary.newSummary')}
            </Button>
          </Center>
        )}

        {!isLoading && filteredItems.length > 0 && (
          <Box>
            {filteredItems.map((s) => (
              <SummaryListItem
                key={s.id}
                summary={s}
                onOpen={() => onSelect(s.id)}
                containerRef={containerRef}
              />
            ))}
          </Box>
        )}
      </Box>

      {summaries.length > 0 && (
        <Flex p={isLessThan768 ? '12px' : '12px 24px'} justifyContent={'flex-start'}>
          <Flex
            gap={'6px'}
            alignItems={'center'}
            color={'{colors.textLink}'}
            cursor={'pointer'}
            onClick={onNew}
          >
            <Svg name={'Plus'} size={'s24'} />
            <Box>{t('summary.newSummary')}</Box>
          </Flex>
        </Flex>
      )}
    </Stack>
  );
};
